// src/components/FAQ/FAQ.jsx
import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import faqsData from "../../data/faqs.json";
import SectionWrapper from "../SectionWrapper/SectionWrapper";

// Rakenteinen tieto muodostetaan samasta datasta kuin näkyvät vastaukset.
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqsData.flatMap((category) =>
    category.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer.replace(/<[^>]+>/g, "").trim()
      }
    }))
  )
};

// Estetään HTML-tagien tulkitseminen JSON-LD-scriptin sisällä.
const faqSchemaJson = JSON.stringify(faqSchema).replace(/</g, "\\u003c");

export default function FAQ() {
  const [open, setOpen] = useState({});
  const openRef = useRef({});
  const headingRef = useRef(null);

  useEffect(() => {
    AOS.init({ once: true, duration: 650 });

    const heading = headingRef.current;

    if (!heading || typeof window.IntersectionObserver !== "function") {
      return;
    }

    // Katseluksi lasketaan vasta osion otsikon näkyminen.
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (
          !entry ||
          !entry.isIntersecting ||
          entry.intersectionRatio < 0.5
        ) {
          return;
        }

        if (typeof window.gaEvent === "function") {
          window.gaEvent("faq_katsottu");
        }

        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    observer.observe(heading);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Avaaminen ja sulkeminen muuttavat alempien osioiden sijaintia.
    AOS.refresh();
  }, [open]);

  const toggle = (key, question) => {
    // Ref pitää tilan ajan tasalla myös nopeissa peräkkäisissä klikkauksissa.
    const next = !openRef.current[key];
    const updated = { ...openRef.current, [key]: next };

    openRef.current = updated;
    setOpen(updated);

    // Tapahtuma lähetetään vain avattaessa, ei suljettaessa.
    if (next && typeof window.gaEvent === "function") {
      window.gaEvent("faq_avattu", { kysymys: question });
    }
  };

  return (
    <>
      <script type="application/ld+json">{faqSchemaJson}</script>

      <SectionWrapper id="useinkysyttya">
        <div
          role="region"
          aria-labelledby="faq-heading"
          className="relative overflow-hidden rounded-[2rem] border border-black/5 bg-white/70 p-3 shadow-xl backdrop-blur-sm sm:p-5 md:p-8 dark:border-white/10 dark:bg-white/5"
        >
          <div className="pointer-events-none absolute -left-24 top-10 h-64 w-64 rounded-full bg-joyrideCyan/15 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 bottom-10 h-64 w-64 rounded-full bg-joyridePurple/20 blur-3xl" />

          <div className="relative z-10 space-y-7 md:space-y-8">
            <div className="text-center" data-aos="fade-down">
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-joyridePurple dark:text-joyrideCyan">
                Kysymyksiä ja vastauksia
              </p>

              <h2
                ref={headingRef}
                id="faq-heading"
                className="font-lobster text-3xl font-bold text-joyridePurple sm:text-4xl md:text-5xl dark:text-joyrideCyan dark:text-shadow-lg"
              >
                Usein kysyttyä
              </h2>

              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-joyrideNavy/80 md:text-lg dark:text-joyrideLight/80">
                Vastauksia Joyriden varaamisesta, keikan sisällöstä,
                musiikkitoiveista ja käytännön järjestelyistä.
              </p>
            </div>

            {faqsData.map((category, catIdx) => (
              <div key={category.category} className="space-y-3 md:space-y-4">
                <h3
                  className="font-lobster text-2xl font-semibold text-joyridePurple md:text-3xl dark:text-joyrideCyan dark:text-shadow-sm"
                 data-aos="fade-right" data-aos-duration="500" data-aos-offset="60"
                >
                  {category.category}
                </h3>

                <div className="overflow-hidden rounded-3xl border border-joyrideNavy/10 bg-white/60 shadow-lg backdrop-blur-sm dark:border-white/10 dark:bg-white/5">
                  {category.faqs.map((item, faqIdx) => {
                    const key = catIdx + "-" + faqIdx;
                    const isOpen = Boolean(open[key]);
                    const contentId = "faq-answer-" + key;
                    const questionId = "faq-question-" + key;

                    return (
                      <div
                        key={key}
                        className="border-b border-joyrideNavy/10 last:border-b-0 dark:border-white/10"
                      >
                        {/* Painike otsikon sisällä toimii Enterillä ja välilyönnillä. */}
                        <h4>
                          <button
                            id={questionId}
                            type="button"
                            onClick={() => toggle(key, item.question)}
                            aria-expanded={isOpen}
                            aria-controls={contentId}
                            className="group flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition hover:bg-joyrideCyan/10 focus-visible:bg-joyrideCyan/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-joyridePurple md:px-6 dark:focus-visible:ring-joyrideCyan"
                          >
                            <span className="min-w-0 flex-1 break-words text-base font-bold text-joyrideNavy transition group-hover:text-joyridePurple md:text-lg dark:text-joyrideLight dark:group-hover:text-joyrideCyan">
                              {item.question}
                            </span>

                            <span
                              className={
                                "grid h-8 w-8 shrink-0 place-items-center rounded-full border border-joyridePurple/20 bg-white/80 text-joyridePurple shadow-sm transition-transform duration-200 motion-reduce:transition-none dark:border-white/10 dark:bg-white/10 dark:text-joyrideCyan " +
                                (isOpen ? "rotate-180" : "")
                              }
                              aria-hidden="true"
                            >
                              ▼
                            </span>
                          </button>
                        </h4>

                        {/* Hidden poistaa suljetun vastauksen myös näppäimistökierrosta. */}
                        <div id={contentId} hidden={!isOpen}>
                          {/* HTML tulee vain ylläpidetystä paikallisesta JSON-tiedostosta. */}
                          <div
                            className="break-words px-4 pb-4 text-base leading-relaxed text-joyrideNavy/85 md:px-6 md:pb-5 md:text-lg dark:text-joyrideLight/85 [&_a]:underline [&_a]:underline-offset-4 [&_a]:transition hover:[&_a]:text-joyridePurple dark:[&_a]:text-joyrideCyan dark:hover:[&_a]:text-joyrideLight"
                            dangerouslySetInnerHTML={{ __html: item.answer }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionWrapper>
    </>
  );
}